/**
 * How tall the picture may be drawn on the climb editor.
 *
 * A photo taken at the wall is portrait — often 9:16 — and drawn at the width of
 * the page it is taller than the screen. The editor then opens on a picture and
 * nothing else: a setter who has just chosen one is shown no sign that naming,
 * grading and colouring the climb are still theirs to do, and the page reads as
 * finished when it has barely started. So the picture gives up the foot of the
 * screen to the first of those fields — the name whole, the head of the grade
 * row under it — which is the page saying, without a word, that it is not done.
 *
 * It is spent as a **max-width** by whoever draws (see `BoardGridCanvas` and
 * `PhotoClimbSurface`), worked back through the photo's own proportions. Every
 * mark on a photo is a percentage of the frame it is drawn in, so that frame has
 * to stay exactly the picture's box: capping the height directly would leave the
 * box taller than the picture and put every circle beside the hold it means. A
 * capped picture therefore narrows and centres itself, giving up the page's
 * width rather than its own shape.
 */

/** The navbar the page sits under: its height plus its border. */
const NAVBAR_HEIGHT = '65px';

/**
 * What is kept back for the form under the picture.
 *
 * Enough for the name field and the top of the grade row — and for the floating
 * pill at the foot of the screen, which is over the page rather than in it, so
 * the room it takes has to be counted here or it would sit on exactly the fields
 * this is reserving.
 */
const FIRST_FIELDS_HEIGHT = '11rem';

/**
 * The cap, as a CSS length.
 *
 * `chrome` is whatever else is stuck to the top of the screen above the picture
 * — the board's toolbar — which comes out of the same budget.
 */
export function editorPhotoMaxHeight(chrome?: string): string {
  const kept = chrome ? `${FIRST_FIELDS_HEIGHT} + ${chrome}` : FIRST_FIELDS_HEIGHT;
  return `calc(100dvh - ${NAVBAR_HEIGHT} - (${kept}))`;
}

/**
 * What the foot of the screen keeps for itself, over the page: the save button
 * (3.5rem tall, 1.5rem off the bottom) or the navigation pill (3rem tall, 0.75rem
 * off the safe area), whichever reaches higher — plus a little air.
 */
const FOOT_CHROME_HEIGHT = 'max(5.5rem, 4.5rem + env(safe-area-inset-bottom))';

/**
 * The height of the picture **and** the fields under it, once a landscape photo
 * has been opened out (see `useExpandablePhoto`).
 *
 * A landscape photo at the width of a phone is a strip a third of the screen
 * tall, with holds too small to hit. Opened out, the two are laid out as one
 * column of exactly this height: the fields take what they need and the picture
 * takes everything else, so the name, the grade and the hold type (or the angle)
 * are still in view above the save button — whichever of them the page shows,
 * without this having to know how tall each one is.
 */
export function editorExpandedHeight(): string {
  return `calc(100dvh - ${NAVBAR_HEIGHT} - ${FOOT_CHROME_HEIGHT})`;
}

/**
 * The picture's box at that cap, or nothing to say when the photo's size is not
 * known yet — the aspect is what turns a height into the width it is applied as.
 */
export function editorPhotoFrameStyle(
  naturalWidth: number,
  naturalHeight: number,
  chrome?: string
): { maxWidth: string } | undefined {
  if (!naturalWidth || !naturalHeight) return undefined;

  return { maxWidth: `calc(${editorPhotoMaxHeight(chrome)} * ${naturalWidth / naturalHeight})` };
}

/**
 * The most of a sideways phone's width the picture may take (`ClimbEditorSplit`);
 * past it the picture covers its column and the sides that do not fit are
 * dragged across.
 */
const SPLIT_PICTURE_MAX_SHARE = '65%';
/** The least the fields keep beside it, whatever the picture would like. */
const SPLIT_FIELDS_MIN_WIDTH = '260px';

/**
 * The picture column's width in the split, as a CSS length: the picture at the
 * screen's full height, capped. The cap alone while its shape is not known yet.
 */
export function splitPictureWidth(aspect: number | null | undefined): string {
  const fit = aspect ? `calc(100dvh * ${aspect})` : SPLIT_PICTURE_MAX_SHARE;
  return `min(${fit}, ${SPLIT_PICTURE_MAX_SHARE}, calc(100% - ${SPLIT_FIELDS_MIN_WIDTH}))`;
}
